"use client";

import { cn } from "@newxsystems/shared";
import type { BlockImageEffect } from "@newxsystems/types";
import { useEffect, useRef, useState } from "react";
import { isWorkerAnimation, type EffectWorkerAnimation } from "./EffectFx";
import { EffectFxHost } from "./EffectFxHost.client";
import { watchEffectVisibility } from "./effect-manager";
import { FX_OPACITY, FX_SURFACE } from "./EffectFxSurface";

/**
 * Animation layer for an ordinary image.
 *
 * It fills whatever box the caller already resolved — for the media components that is
 * the frame the aspect-ratio class produced, and the frame's own default when the image
 * carries no ratio — so the effect never invents its own size. The canvas is built (and
 * a still frame drawn) once the frame is within a viewport of the screen, and only runs
 * while it intersects the viewport (see `effect-manager.ts`), so it is ready the moment
 * it scrolls in without off-screen overlays burning frames; with reduced motion, or on a
 * box too small to read, the plain image stays.
 */

/** Below this the shaders read as noise rather than motion; the image stays plain. */
const MIN_BOX_WIDTH = 180;
const MIN_BOX_HEIGHT = 110;

export const EffectOverlay = ({
  animation,
  className,
}: {
  animation?: BlockImageEffect | null;
  className?: string;
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setActive(false);
    setPlaying(false);
    setReady(false);

    const frame = frameRef.current;
    if (!animation || !frame || !isWorkerAnimation(animation)) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;

    const box = frame.getBoundingClientRect();
    if (box.width < MIN_BOX_WIDTH || box.height < MIN_BOX_HEIGHT) return undefined;

    return watchEffectVisibility(frame, {
      warm: () => setActive(true),
      cool: () => {
        setActive(false);
        setReady(false);
      },
      play: () => setPlaying(true),
      pause: () => setPlaying(false),
    });
  }, [animation]);

  if (!animation) return null;

  return (
    // `pointer-events-none` keeps the image clickable; pointer-driven styles still track
    // the mouse because the host listens on the window rather than on the canvas.
    <div
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}
      ref={frameRef}
      style={{ opacity: FX_OPACITY[animation] ?? 1 }}
    >
      {active && isWorkerAnimation(animation) ? (
        // The first worker frame lands a beat after mount; fading in hides that gap so
        // the effect never pops over the photograph.
        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-500 ease-out motion-reduce:transition-none",
            ready ? "opacity-100" : "opacity-0",
          )}
        >
          <EffectFxHost
            animation={animation as EffectWorkerAnimation}
            containerClass={FX_SURFACE[animation]?.containerClass}
            containerStyle={FX_SURFACE[animation]?.containerStyle}
            onFailed={() => setActive(false)}
            onReady={() => setReady(true)}
            paused={!playing}
            worker
          />
        </div>
      ) : null}
    </div>
  );
};

EffectOverlay.displayName = "Effects::EffectOverlay";
